import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  TablePagination,
} from "@mui/material";
import { EditIcon, TrashIcon } from "./icons";
import { useTranslation } from "react-i18next";
import { useStyles } from "./style";
import { User } from "../../types";

interface SelectableTableProps {
  data: User[];
  query: string;
  highlightText?: (text: string, query: string) => React.ReactNode;
  selectedRow: string | null;
  setSelectedRow: React.Dispatch<React.SetStateAction<string | null>>;
  onEditClick: (account: User) => void;
  selectedAccount: User | null;
  setSelectedAccount: React.Dispatch<React.SetStateAction<User | null>>;
}

export const SelectableTable: React.FC<SelectableTableProps> = ({
  data,
  query,
  highlightText,
  selectedRow,
  setSelectedRow,
  onEditClick,
  selectedAccount,
  setSelectedAccount,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleRowClick = (row: User) => {
    // Check if selectedAccount is not null, reset it to null before selecting the row
    // we do that to close AccountDetails if its openend
    if (selectedAccount !== null) {
      setSelectedAccount(null); // Close account details if it's open
    }

    setSelectedRow(selectedRow === row.id ? null : row.id);
  };

  // Pagination handlers
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Paper className={classes.tableContainer}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHead}>
                {t("selectableTable.email")}
              </TableCell>
              <TableCell className={classes.tableHead}>
                {t("selectableTable.firstName")}
              </TableCell>
              <TableCell className={classes.tableHead}>
                {t("selectableTable.lastName")}
              </TableCell>
              <TableCell className={classes.tableHead}>
                {t("selectableTable.apaSerial")}
              </TableCell>
              <TableCell className={classes.tableHead}>
                {t("selectableTable.mobile")}
              </TableCell>
              <TableCell className={classes.tableHead}>
                {t("selectableTable.subscription")}
              </TableCell>
              <TableCell className={classes.tableHead}>
                {t("selectableTable.status")}
              </TableCell>
              <TableCell className={classes.tableHead}>
                {t("selectableTable.usage")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row) => {
                const [firstName, ...lastNameParts] = row.name?.split(" ") ?? [
                  "",
                  "",
                ];
                const lastName = lastNameParts.join(" ");

                return (
                  <TableRow
                    key={row.id}
                    onClick={() => handleRowClick(row)}
                    sx={{
                      cursor: "pointer",
                      border:
                        selectedRow === row.id ? "2px solid #00ADEF" : "none",
                      position: "relative",
                    }}
                  >
                    <TableCell>
                      {highlightText
                        ? highlightText(row.email ?? "-", query)
                        : row.email ?? "-"}
                    </TableCell>
                    <TableCell>
                      {highlightText
                        ? highlightText(firstName || "-", query)
                        : firstName || "-"}
                    </TableCell>
                    <TableCell>
                      {highlightText
                        ? highlightText(lastName || "-", query)
                        : lastName || "-"}
                    </TableCell>
                    <TableCell>
                      {highlightText
                        ? highlightText(row.apaSerial ?? "-", query)
                        : row.apaSerial ?? "-"}
                    </TableCell>
                    <TableCell>
                      {highlightText
                        ? highlightText(row.phone ?? "-", query)
                        : row.phone ?? "-"}
                    </TableCell>
                    <TableCell>
                      {highlightText
                        ? highlightText(row.subscription ?? "-", query)
                        : row.subscription ?? "-"}
                    </TableCell>
                    <TableCell>
                      {highlightText
                        ? highlightText(row.status ?? "-", query)
                        : row.status ?? "-"}
                    </TableCell>
                    <TableCell sx={{ position: "relative" }}>
                      {highlightText
                        ? highlightText(row.usage ?? "-", query)
                        : row.usage ?? "-"}
                      {selectedRow === row.id && !selectedAccount && (
                        <Box className={classes.selectedRowWrapper}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent row selection
                              onEditClick(row);
                            }}
                          >
                            <EditIcon width={32} height={32} />
                          </IconButton>
                          <IconButton size="small">
                            <TrashIcon width={32} height={32} />
                          </IconButton>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Component */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};
